import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api';
import './estilodashuser.css';

function formatMoney(value, locale) {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'BRL' }).format(Number(value || 0));
}

function formatDate(value, locale) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(locale);
}

function formatMesReferencia(value, locale) {
  if (!value || !String(value).includes('-')) return value || '—';
  const [ano, mes] = String(value).split('-');
  return new Date(Date.UTC(Number(ano), Number(mes) - 1, 1)).toLocaleDateString(locale, {
    month: '2-digit', year: 'numeric', timeZone: 'UTC',
  });
}

function getStatusBadgeClass(status) {
  const s = String(status || '').toLowerCase();

  if (s === 'pago') return 'qt-badge qt-badge-success';
  if (s === 'isento') return 'qt-badge qt-badge-info';
  if (['cancelado', 'vencido', 'teste vencido', 'expirado', 'em atraso'].includes(s)) {
    return 'qt-badge qt-badge-danger';
  }
  if (['aguardando pagamento', 'em aberto', 'pendente'].includes(s)) {
    return 'qt-badge qt-badge-warning';
  }

  return 'qt-badge qt-badge-neutral';
}

export default function ListarPagamentos({ userId }) {
  const { t, i18n } = useTranslation();
  const [pagamentos, setPagamentos] = useState([]);
  const [assinaturas, setAssinaturas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const locale = i18n.resolvedLanguage === 'en' ? 'en-US' : 'pt-BR';
  const getStatusLabel = (status) => {
    const normalized = String(status || '').toLowerCase();
    return t(`dashboard.payments.statuses.${normalized}`, { defaultValue: status || '—' });
  };

  useEffect(() => {
    if (userId) carregar();
  }, [userId]);

  const carregar = async () => {
    try {
      setLoading(true);
      setErro('');

      const [respPag, respAss] = await Promise.all([
        api.get('/api/meus-pagamentos'),
        api.get('/api/minhas-assinaturas'),
      ]);

      setPagamentos(respPag.data || []);
      setAssinaturas(respAss.data || []);
    } catch (err) {
      console.error('Erro ao carregar pagamentos do cliente:', err);
      setErro(t('dashboard.payments.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const resumoFinanceiro = useMemo(() => {
    const pagos = pagamentos.filter((p) => String(p.status || '').toLowerCase() === 'pago');
    const abertos = pagamentos.filter((p) =>
      ['aguardando pagamento', 'em aberto', 'pendente'].includes(String(p.status || '').toLowerCase())
    );
    const totalPago = pagos.reduce((acc, p) => acc + Number(p.valor || p.valor_calculado || 0), 0);
    const totalAberto = abertos.reduce((acc, p) => acc + Number(p.valor_calculado || p.valor || 0), 0);

    return {
      total: pagamentos.length,
      pagos: pagos.length,
      emAberto: abertos.length,
      totalPago,
      totalAberto,
    };
  }, [pagamentos]);

  const linhas = useMemo(() => {
    return pagamentos
      .map((p) => {
        const assinatura =
          assinaturas.find((a) => Number(a.id) === Number(p.assinatura_id)) || null;

        return {
          ...p,
          assinatura,
        };
      })
      .sort((a, b) => {
        const mesA = String(a.mes_referencia || '');
        const mesB = String(b.mes_referencia || '');
        if (mesA !== mesB) return mesB.localeCompare(mesA);

        const dataA = new Date(a.data_pagamento || a.created_on || 0).getTime();
        const dataB = new Date(b.data_pagamento || b.created_on || 0).getTime();
        return dataB - dataA;
      });
  }, [pagamentos, assinaturas]);

  return (
    <div className="user-payments-wrap">
      <div className="user-payments-card">
        <div className="user-payments-header">
          <h3>{t('dashboard.payments.title')}</h3>
          <button type="button" className="lic-btn-outline qt-btn qt-btn-secondary" onClick={carregar}>
            {t('dashboard.payments.reload')}
          </button>
        </div>

        <div className="cliente-help-box" style={{ marginBottom: 14 }}>
          <strong>{t('dashboard.payments.helpTitle')}</strong> {t('dashboard.payments.help')}
        </div>

        {erro && <div className="alert-box alert-error">{erro}</div>}

        <div className="lic-kpis lic-kpis-compact">
          <div className="lic-kpi">
            <div className="lic-kpi-label">{t('dashboard.payments.entries')}</div>
            <div className="lic-kpi-value">{resumoFinanceiro.total}</div>
          </div>
          <div className="lic-kpi">
            <div className="lic-kpi-label">{t('dashboard.payments.paid')}</div>
            <div className="lic-kpi-value">{resumoFinanceiro.pagos}</div>
          </div>
          <div className="lic-kpi">
            <div className="lic-kpi-label">{t('dashboard.payments.open')}</div>
            <div className="lic-kpi-value">{resumoFinanceiro.emAberto}</div>
          </div>
          <div className="lic-kpi">
            <div className="lic-kpi-label">{t('dashboard.payments.totalPaid')}</div>
            <div className="lic-kpi-value">{formatMoney(resumoFinanceiro.totalPago, locale)}</div>
          </div>
        </div>

        {loading ? (
          <p className="lic-loading">{t('dashboard.payments.loading')}</p>
        ) : (
          <div className="payments-list">
            {linhas.length === 0 ? (
              <div className="cliente-empty-box">
                {t('dashboard.payments.empty')}
              </div>
            ) : (
              linhas.map((p) => (
                <div key={p.id} className="payment-item-card">
                  <div className="payment-item-top">
                    <div>
                      <div className="payment-item-title">
                        {p.assinatura?.nome_plano || t('dashboard.payments.accountCharge')}
                      </div>
                      <div className="payment-item-subtitle">
                        {t('dashboard.payments.referenceMonth')}: <strong>{formatMesReferencia(p.mes_referencia, locale)}</strong>
                      </div>
                    </div>

                    <span className={getStatusBadgeClass(p.status)}>
                      {getStatusLabel(p.status)}
                    </span>
                  </div>

                  <div className="payment-item-grid">
                    <div className="payment-item-line qt-payment-field">
                      <span>{t('dashboard.payments.value')}</span>
                      <strong>{formatMoney(p.valor || p.valor_calculado || 0, locale)}</strong>
                    </div>

                    <div className="payment-item-line qt-payment-field">
                      <span>{t('dashboard.payments.date')}</span>
                      <strong>{formatDate(p.data_pagamento, locale)}</strong>
                    </div>

                    <div className="payment-item-line qt-payment-field">
                      <span>{t('dashboard.payments.method')}</span>
                      <strong>{p.metodo_pagamento || '—'}</strong>
                    </div>

                    <div className="payment-item-line qt-payment-field">
                      <span>{t('dashboard.payments.source')}</span>
                      <strong>{p.origem_cobranca || '—'}</strong>
                    </div>
                  </div>

                  <div className="payment-description-box">
                    <div className="payment-description-title">{t('dashboard.payments.description')}</div>
                    <div className="payment-description-text">
                      {p.descricao || t('dashboard.payments.noDescription')}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <div className="lic-footnote">
          {t('dashboard.payments.footnote')}
        </div>
      </div>
    </div>
  );
}
